import React, { useState } from 'react';
import './ParticipantPanel.scss';
import { useTranslation } from 'react-i18next';
import ChatUserAvatar from './ChatUserAvatar';
import VerifiedShieldBadge from '../common/VerifiedShieldBadge/VerifiedShieldBadge';
import { apiRequest } from '../utils/api';
import { CloseIcon } from '../common/CloseIcon';

interface Participant {
  userId: string;
  name: string;
  avatar: string | null;
  role: string;
  marketplaceRole?: 'BUYER' | 'SELLER' | null;
  roleLabel?: string;
  isOriginal?: boolean;
  isSelf: boolean;
  hasSentMessage?: boolean;
  addedByCurrentUser?: boolean;
  verified?: boolean;
}

interface ParticipantPanelProps {
  conversationId: string;
  participants: Participant[];
  pendingInviteEmail?: string | null;
  currentUserRole: string;
  canInvite?: boolean;
  onClose: () => void;
  onInvite: () => void;
  onLeave?: () => void;
  refreshConversations?: () => void;
  onRefreshMessages?: () => void;
}

const ParticipantPanel: React.FC<ParticipantPanelProps> = ({
  conversationId,
  participants,
  pendingInviteEmail,
  currentUserRole,
  canInvite,
  onClose,
  onInvite,
  onLeave,
  refreshConversations,
  onRefreshMessages
}) => {
  const { t } = useTranslation();
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const [isLeaving, setIsLeaving] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selfParticipant = participants.find(p => p.isSelf);

  const handleRemove = async (targetUserId: string) => {
    setError(null);
    try {
      await apiRequest(`/conversations/${conversationId}/participants/${targetUserId}`, {
        method: 'DELETE',
      });
      setMenuOpenFor(null);
      refreshConversations?.();
      onRefreshMessages?.();
    } catch (err: any) {
      setError(err?.error || 'Failed to remove participant');
    }
  };

  const handleLeave = async () => {
    if (!selfParticipant) return;
    setIsLeaving(true);
    setError(null);
    try {
      await apiRequest(`/conversations/${conversationId}/participants/${selfParticipant.userId}`, {
        method: 'DELETE',
      });
      refreshConversations?.();
      onLeave?.();
    } catch (err: any) {
      setError(err?.error || 'Failed to leave conversation');
    } finally {
      setIsLeaving(false);
    }
  };

  return (
    <div className="participant-panel-overlay" onClick={onClose}>
      <div className="participant-panel" onClick={e => e.stopPropagation()}>
        <div className="participant-panel__header">
          <h3>{t('messages.participants.title')}</h3>
          <button className="participant-panel__close" onClick={onClose}>
            <CloseIcon size={16} />
          </button>
        </div>

        {error && <div className="participant-panel__error">{error}</div>}

        <div className="participant-panel__list">
          {participants.map(participant => {
            const isAnonymized = !participant.name && !participant.isSelf;

            const roleLabelText = (rl?: string) => {
              switch (rl) {
                case 'seller': return t('messages.seller');
                case 'buyer': return t('messages.buyer');
                case 'seller_participant': return t('messages.sellerParticipant');
                case 'buyer_participant': return t('messages.buyerParticipant');
                case 'participant': return t('messages.participants.participant');
                default: return t('messages.participants.participant');
              }
            };

            const displayName = isAnonymized
              ? roleLabelText(participant.roleLabel)
              : participant.name;

            return (
              <div key={participant.userId} className="participant-panel__item">
                <div className="participant-panel__avatar">
                  {participant.avatar ? (
                    <>
                      <img src={participant.avatar} alt={displayName} />
                      {participant.verified && <VerifiedShieldBadge size="sm" />}
                    </>
                  ) : (
                    <ChatUserAvatar
                      name={isAnonymized ? "" : displayName}
                      height={40}
                      width={40}
                      verified={!!participant.verified}
                    />
                  )}
                </div>
                <div className="participant-panel__info">
                  <span className="participant-panel__name">
                    {displayName}
                    {participant.isSelf && <span className="participant-panel__you"> {t('messages.participants.you')}</span>}
                  </span>
                </div>
                {!participant.isSelf && participant.addedByCurrentUser && (
                  <div className="participant-panel__menu-container">
                    <button
                      className="participant-panel__menu-trigger"
                      onClick={() => setMenuOpenFor(menuOpenFor === participant.userId ? null : participant.userId)}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <circle cx="8" cy="3" r="1.5" />
                        <circle cx="8" cy="8" r="1.5" />
                        <circle cx="8" cy="13" r="1.5" />
                      </svg>
                    </button>
                    {menuOpenFor === participant.userId && (
                      <div className="participant-panel__menu">
                        <button
                          className="participant-panel__menu-danger"
                          onClick={() => handleRemove(participant.userId)}
                        >
                          {t('messages.participants.removeParticipant')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {pendingInviteEmail && (
            <div className="participant-panel__item participant-panel__item--pending">
              <div className="participant-panel__avatar">
                <ChatUserAvatar name="" height={40} width={40} />
              </div>
              <div className="participant-panel__info">
                <span className="participant-panel__name">
                  {pendingInviteEmail}
                  <span className="participant-panel__pending-badge">
                    {' '}{t('messages.participants.pendingInvite')}
                  </span>
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="participant-panel__actions">
          {canInvite && (
            <button className="participant-panel__add-button" onClick={onInvite}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M9 3V15M3 9H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {t('messages.participants.addPeople')}
            </button>
          )}
          {selfParticipant && !selfParticipant.isOriginal && (
            !showLeaveConfirm ? (
              <button
                className="participant-panel__leave-button"
                onClick={() => setShowLeaveConfirm(true)}
              >
                {t('messages.participants.leaveConversation')}
              </button>
            ) : (
              <div className="participant-panel__leave-confirm">
                <p>{t('messages.participants.leaveConfirmText')}</p>
                <div className="participant-panel__leave-confirm-actions">
                  <button onClick={() => setShowLeaveConfirm(false)} disabled={isLeaving}>
                    {t('messages.cancel')}
                  </button>
                  <button
                    className="participant-panel__leave-confirm-button"
                    onClick={handleLeave}
                    disabled={isLeaving}
                  >
                    {t('messages.participants.leaveConfirmButton')}
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ParticipantPanel;
